/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { WidgetSize } from "./widget-declarations";
import { useAuth } from "@/hooks/auth";
import supabase from "@/lib/supabase";
import { useLanguage } from "@/hooks/useLanguage";

interface UserCardWidgetProps {
  size?: WidgetSize;
}

// واجهة لإحصائيات المستخدم
interface UserStats {
  projects: number;
  tasks: number;
  completed: number;
}

// واجهة لمعلومات الدورات
interface CourseInfo {
  total: number;
  completed: number;
  inProgress: number;
  certificates: number;
}

// واجهة لبيانات المستخدم الكاملة
interface UserData {
  name: string;
  email: string;
  title: string;
  avatar: string;
  department: string;
  joinDate: string;
  stats: UserStats;
  skills: string[];
  description: string;
  greeting: string;
  courses: CourseInfo;
  lastCourseAccessed?: string;
  nextDeadline?: string;
}

const UserCardWidget: React.FC<UserCardWidgetProps> = ({ size = "small" }) => {
  // استخدام سياق المصادقة للحصول على بيانات المستخدم
  const { user: authUser } = useAuth();
  const { t } = useLanguage();
  // حالة لتخزين البيانات المعالجة للمستخدم
  const [userData, setUserData] = useState<UserData | null>(null);
  // حالة للتحكم في حالة التحميل
  const [loading, setLoading] = useState<boolean>(true);
  // حالة للتحكم في إظهار الرأس
  const [showHeader, setShowHeader] = useState<boolean>(false);

  // التحقق من الشاشة الصغيرة والحفاظ على تحديثها
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== "undefined" && window.innerWidth < 640
  );

  // تحية المستخدم حسب الوقت
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return t("greeting.morning");
    } else if (hour >= 12 && hour < 17) {
      return t("greeting.afternoon");
    } else if (hour >= 17 && hour < 21) {
      return t("greeting.evening");
    } else {
      return t("greeting.night");
    }
  };

  // تعيين بيانات المستخدم الافتراضية
  const setDefaultUserData = () => {
    const firstName =
      authUser?.first_name || authUser?.user_metadata?.first_name || "";
    const lastName =
      authUser?.last_name || authUser?.user_metadata?.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim() || "مستخدم جديد";

    // استخدام صورة الملف الشخصي المناسبة بناءً على الجنس
    let defaultAvatar = "https://randomuser.me/api/portraits/lego/1.jpg";

    // التحقق من وجود معلومات الجنس في بيانات المستخدم
    if (authUser?.gender === "female") {
      defaultAvatar = "https://i.ibb.co/Z1bXn3T/female-avatar.webp"; // أنثى
    } else if (authUser?.gender === "male") {
      defaultAvatar = "https://i.ibb.co/TXYGdKB/male-avatar.jpg"; // ذكر
    } else if (authUser?.user_metadata?.gender === "female") {
      defaultAvatar = "https://i.ibb.co/Z1bXn3T/female-avatar.webp"; // أنثى من البيانات الوصفية
    } else if (authUser?.user_metadata?.gender === "male") {
      defaultAvatar = "https://i.ibb.co/TXYGdKB/male-avatar.jpg"; // ذكر من البيانات الوصفية
    }

    console.log("Setting default user data with avatar:", defaultAvatar);

    setUserData({
      name: fullName,
      email: authUser?.email || "بريد إلكتروني غير محدد",
      title: "موظف",
      avatar: defaultAvatar,
      department: "قسم غير محدد",
      joinDate: "ينضم حديثاً",
      stats: {
        projects: 0,
        tasks: 0,
        completed: 0,
      },
      skills: ["تطوير", "تصميم"],
      description: "عضو جديد في النظام.",
      greeting: getGreeting(),
      courses: {
        total: 0,
        completed: 0,
        inProgress: 0,
        certificates: 0,
      },
      lastCourseAccessed: "لم يتم الوصول إلى أي دورة مؤخرًا",
      nextDeadline: "لا توجد مواعيد نهائية قادمة",
    });
  };

  // استعلام بيانات المستخدم من قاعدة البيانات
  useEffect(() => {
    const fetchUserData = async () => {
      if (!authUser?.email) {
        console.log("No user email available, cannot fetch user data");
        setDefaultUserData();
        return;
      }

      try {
        setLoading(true);

        // جلب بيانات الموظف - استخدام email للمطابقة بدلاً من id
        // الايميل هو المفتاح المشترك بين جدول auth.users وجدول employees
        const { data: employee, error } = await supabase
          .from("employees")
          .select(
            `
            id,
            first_name,
            last_name,
            email,
            profile_image,
            job_title,
            department_id,
            created_at,
            gender,
            department_id(name)
          `
          )
          .eq("email", authUser.email)
          .single();

        if (error) {
          console.error("خطأ في جلب بيانات المستخدم:", error);
          // استخدام بيانات افتراضية في حالة الخطأ
          setDefaultUserData();
          return;
        }

        if (!employee) {
          console.warn("لم يتم العثور على بيانات الموظف");
          setDefaultUserData();
          return;
        }

        // جلب إحصائيات المستخدم (مثل المهام والمشاريع)
        const { data: stats, error: statsError } = await supabase
          .from("employees")
          .select("*")
          .eq("id", authUser.id)
          .single();

        // استخدام إحصائيات افتراضية في حالة عدم وجود بيانات أو حدوث خطأ
        const userStats: UserStats =
          stats && !statsError
            ? {
                projects: stats.projects_count || 0,
                tasks: stats.tasks_count || 0,
                completed: stats.completed_tasks || 0,
              }
            : {
                projects: Math.floor(Math.random() * 20),
                tasks: Math.floor(Math.random() * 50),
                completed: Math.floor(Math.random() * 30),
              };

        // جلب مهارات المستخدم
        const { data: employeeData, error: employeeError } = await supabase
          .from("employees")
          .select("metadata->skills")
          .eq("id", authUser.id)
          .single();

        // استخدام مهارات افتراضية في حالة عدم وجود بيانات أو حدوث خطأ
        const skills =
          employeeData &&
          !employeeError &&
          employeeData.skills &&
          employeeData.skills.length > 0
            ? employeeData.skills
            : ["React", "TypeScript", "UI/UX", "Supabase"];

        // تنسيق تاريخ الانضمام
        const joinDate = employee.created_at
          ? new Date(employee.created_at).toLocaleDateString("ar-SA", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : "تاريخ غير محدد";

        // تجميع البيانات النهائية للمستخدم
        console.log("Employee data retrieved successfully:", employee);

        // استخدام صورة الملف الشخصي المناسبة بناءً على الجنس إذا لم تكن متوفرة
        let avatarUrl = employee.profile_image;
        if (!avatarUrl) {
          if (employee.gender === "female") {
            avatarUrl = "https://i.ibb.co/XxmL89tB/female-avatar.png "; // Default female avatar
          } else if (employee.gender === "male") {
            avatarUrl = "https://i.ibb.co/TXYGdKB/male-avatar.jpg"; // Default male avatar
          } else {
            avatarUrl = "https://i.ibb.co/XxmL89tB/female-avatar.png"; // Generic avatar
          }
        }

        // استخراج اسم القسم من العلاقة إذا كان متاحًا
        const departmentName =
          employee.department_id && employee.department_id.name
            ? employee.department_id.name
            : "قسم غير محدد";

        // جلب معلومات الدورات التدريبية للمستخدم
        const { data: coursesData, error: coursesError } = await supabase
          .from("enrollments")
          .select(
            `
            course_id,
            courses (
              id,
              title,
              thumbnail,
              duration
            ),
            progress,
            completion_date,
            is_certified,
            last_accessed_at
          `
          )
          .eq("employee_id", authUser.id);

        // معلومات الدورات الافتراضية
        const courseInfo: CourseInfo = {
          total: 0,
          completed: 0,
          inProgress: 0,
          certificates: 0,
        };

        let lastCourseAccessed = undefined;
        const nextDeadline = undefined;

        // حساب إحصائيات الدورات إذا كانت البيانات متوفرة
        if (coursesData && !coursesError && coursesData.length > 0) {
          courseInfo.total = coursesData.length;
          courseInfo.completed = coursesData.filter(
            (course) => course.progress === 100
          ).length;
          courseInfo.inProgress = coursesData.filter(
            (course) => course.progress > 0 && course.progress < 100
          ).length;
          courseInfo.certificates = coursesData.filter(
            (course) => course.is_certified
          ).length;

          // العثور على آخر دورة تم الوصول إليها
          const lastAccessed = coursesData
            .filter((course) => course.last_accessed_at)
            .sort(
              (a, b) =>
                new Date(b.last_accessed_at).getTime() -
                new Date(a.last_accessed_at).getTime()
            )[0];

          if (lastAccessed) {
            // جلب اسم الدورة التي تم الوصول إليها مؤخرًا
            const { data: courseDetails } = await supabase
              .from("courses")
              .select("title")
              .eq("id", lastAccessed.course_id)
              .single();

            if (courseDetails) {
              lastCourseAccessed = courseDetails.title;
            }
          }
        }

        // Update the user data state with all the retrieved information
        setUserData({
          name: `${employee.first_name || ""} ${
            employee.last_name || ""
          }`.trim(),
          email: employee.email || authUser?.email || "",
          title: employee.job_title || "موظف",
          avatar: avatarUrl,
          department: departmentName,
          joinDate: joinDate,
          stats: userStats,
          skills: skills,
          greeting: getGreeting(),
          courses: courseInfo,
          lastCourseAccessed:
            lastCourseAccessed || "لم يتم الوصول إلى أي دورة مؤخرًا",
          nextDeadline: nextDeadline || "لا توجد مواعيد نهائية قادمة",
        });

        setLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setDefaultUserData();
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [authUser]);

  // استماع لتغيير حجم الشاشة
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // تحديد محتوى الويدجت حسب الحجم
  const renderContent = () => {
    // استخدام الحجم الصغير للشاشات الصغيرة بغض النظر عن حجم الويدجت المحدد
    const effectiveSize = isSmallScreen ? "small" : size;

    // إذا كان البيانات قيد التحميل أو غير متوفرة، عرض مؤشر تحميل
    if (loading || !userData) {
      return (
        <div className="flex flex-col items-center justify-center h-full w-full">
          <div className="w-12 h-12 rounded-full bg-white/10 animate-pulse mb-3"></div>
          <div className="h-4 w-24 bg-white/10 rounded animate-pulse mb-2"></div>
          <div className="h-3 w-32 bg-white/10 rounded animate-pulse"></div>
        </div>
      );
    }

    switch (effectiveSize) {
      case "small":
        return (
          <div className="text-center w-full">
            {/* صورة المستخدم في شكل سحابة صغيرة */}
            <div className="relative mb-3 inline-block">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full blur opacity-70"></div>
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-2 border-white/50 overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600">
                  <img
                    src={userData.avatar}
                    alt={userData.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* التحية حسب الوقت */}
            <p className="text-blue-300 text-xs mb-1">{userData.greeting}</p>

            {/* اسم المستخدم */}
            <h3 className="text-white font-bold text-base mb-0.5">
              {userData.name}
            </h3>

            {/* البريد الإلكتروني */}
            <p className="text-gray-400 text-xs mb-2">{userData.email}</p>

            {/* المسمى الوظيفي */}
            <p className="text-white/90 text-xs font-medium bg-white/10 px-2 py-1 rounded-full inline-block">
              {userData.title}
            </p>
          </div>
        );

      case "medium":
        return (
          <div className="w-full">
            <div className="flex items-start mb-3">
              {/* صورة المستخدم في شكل سحابة صغيرة */}
              <div className="relative mr-3 rtl:mr-0 rtl:ml-3">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full blur opacity-70"></div>
                <div className="relative">
                  <div className="w-16 h-16 rounded-full border-2 border-white/50 overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600">
                    <img
                      src={userData.avatar}
                      alt={userData.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1">
                {/* التحية حسب الوقت */}
                <p className="text-blue-300 text-xs mb-1 inline-block bg-blue-500/10 px-2 py-0.5 rounded-full">
                  {userData.greeting}
                </p>

                {/* اسم المستخدم */}
                <h3 className="text-white font-bold text-lg mb-0.5">
                  {userData.name}
                </h3>

                {/* البريد الإلكتروني */}
                <p className="text-gray-400 text-xs mb-1">{userData.email}</p>

                {/* المسمى الوظيفي والقسم */}
                <div className="flex flex-wrap gap-1 mt-1">
                  <p className="text-white/90 text-xs font-medium bg-white/10 px-2 py-0.5 rounded-full inline-block">
                    {userData.title}
                  </p>
                  <p className="text-white/90 text-xs font-medium bg-white/10 px-2 py-0.5 rounded-full inline-block">
                    {userData.department}
                  </p>
                </div>
              </div>
            </div>

            {/* إحصائيات المستخدم */}
            <div className="grid grid-cols-3 gap-2 border-t border-white/10 pt-3 mt-1">
              <div className="text-center">
                <p className="text-xs text-gray-400">المشاريع</p>
                <p className="text-white font-bold text-lg">
                  {userData.stats.projects}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-400">المهام</p>
                <p className="text-white font-bold text-lg">
                  {userData.stats.tasks}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-400">المكتملة</p>
                <p className="text-white font-bold text-lg">
                  {userData.stats.completed}
                </p>
              </div>
            </div>
          </div>
        );

      case "large":
        return (
          <div className="w-full">
            <div className="flex mb-4">
              {/* صورة المستخدم في شكل سحابة صغيرة */}
              <div className="relative mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full blur opacity-70"></div>
                <div className="relative">
                  <div className="w-20 h-20 rounded-full border-2 border-white/50 overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600">
                    <img
                      src={userData.avatar}
                      alt={userData.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1">
                {/* التحية حسب الوقت */}
                <p className="text-blue-300 text-sm mb-1 inline-block bg-blue-500/10 px-2 py-0.5 rounded-full">
                  {userData.greeting}
                </p>

                {/* اسم المستخدم */}
                <h3 className="text-white font-bold text-xl mb-1">
                  {userData.name}
                </h3>

                {/* البريد الإلكتروني */}
                <p className="text-gray-400 text-sm mb-2">{userData.email}</p>

                {/* المسمى الوظيفي والقسم */}
                <div className="flex flex-wrap gap-1 mb-2">
                  <p className="text-white/90 text-xs font-medium bg-white/10 px-2 py-1 rounded-full inline-block">
                    {userData.title}
                  </p>
                  <p className="text-white/90 text-xs font-medium bg-white/10 px-2 py-1 rounded-full inline-block">
                    {userData.department}
                  </p>
                </div>

                {/* تاريخ الانضمام */}
                <p className="text-gray-400 text-xs">{userData.joinDate}</p>
              </div>
            </div>

            {/* وصف المستخدم */}
            <div className="mb-3">
              <p className="text-white/80 text-xs leading-relaxed">
                {userData.description}
              </p>
            </div>

            {/* المهارات */}
            <div className="mb-3">
              <p className="text-gray-400 text-xs mb-1">المهارات</p>
              <div className="flex flex-wrap gap-1">
                {userData.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="bg-white/10 text-white text-xs px-2 py-0.5 rounded-full"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* إحصائيات المستخدم */}
            <div className="grid grid-cols-3 gap-2 border-t border-white/10 pt-3">
              <div className="text-center">
                <p className="text-xs text-gray-400">المشاريع</p>
                <p className="text-white font-bold text-lg">
                  {userData.stats.projects}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-400">المهام</p>
                <p className="text-white font-bold text-lg">
                  {userData.stats.tasks}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-400">المكتملة</p>
                <p className="text-white font-bold text-lg">
                  {userData.stats.completed}
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // اختيار حجم الهامش والحشو بناءً على حجم الشاشة
  // const padding = isSmallScreen ? "p-2" : "p-3"; // Unused, can be removed

  // تنسيق حالة التحميل
  const containerClasses = `
    w-full h-full bg-black/20 backdrop-blur-md rounded-xl 
    flex flex-col items-center justify-center
    relative overflow-hidden
    ${loading ? "animate-pulse" : ""}
    border border-white/20
  `;

  // iCloud style rendering
  const renderICloudStyleCard = () => {
    if (loading || !userData) {
      return (
        <div className="flex flex-col items-center justify-center h-full w-full p-6">
          <div className="w-16 h-16 rounded-full bg-white/10 animate-pulse mb-4"></div>
          <div className="h-5 w-32 bg-white/10 rounded animate-pulse mb-2"></div>
          <div className="h-4 w-40 bg-white/10 rounded animate-pulse"></div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full w-full text-center px-4 py-3">
        {/* Avatar and basic info section */}
        <div className="mb-2 flex flex-col items-center">
          <div className="mb-2 relative">
            <div className="w-20 h-20 rounded-full overflow-hidden ring-2 ring-white/30">
              <img
                src={userData.avatar}
                alt={userData.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* User Name */}
          <h2 className="text-white text-xl font-bold mb-0.5">
            {userData.name}
          </h2>

          {/* Email */}
          <p className="text-white/70 text-xs mb-1">{userData.email}</p>

          {/* Department */}
          <div className="bg-white/10 rounded-full px-3 py-1 text-white text-xs mb-2">
            {userData.department}
          </div>
        </div>

        {/* Course Information Section */}
        <div className="mt-1 w-full border-t border-white/10 pt-2 pb-1">
          <h3 className="text-white/90 text-sm font-medium mb-1">
            معلومات الدورات
          </h3>

          {/* Course Stats Row */}
          <div className="grid grid-cols-4 gap-1 mb-2">
            <div className="text-center">
              <p className="text-white/60 text-xs">الكلي</p>
              <p className="text-white font-bold">{userData.courses.total}</p>
            </div>
            <div className="text-center">
              <p className="text-white/60 text-xs">المكتملة</p>
              <p className="text-white font-bold">
                {userData.courses.completed}
              </p>
            </div>
            <div className="text-center">
              <p className="text-white/60 text-xs">قيد التقدم</p>
              <p className="text-white font-bold">
                {userData.courses.inProgress}
              </p>
            </div>
            <div className="text-center">
              <p className="text-white/60 text-xs">الشهادات</p>
              <p className="text-white font-bold">
                {userData.courses.certificates}
              </p>
            </div>
          </div>

          {/* Last Course Accessed */}
          {userData.lastCourseAccessed && (
            <div className="text-start mb-1.5">
              <p className="text-white/60 text-xs mb-0.5">
                آخر دورة تم الوصول إليها:
              </p>
              <p className="text-white text-xs font-medium bg-blue-500/10 px-2 py-1 rounded-md line-clamp-1">
                {userData.lastCourseAccessed}
              </p>
            </div>
          )}

          {/* Next Deadline */}
          {userData.nextDeadline && (
            <div className="text-start">
              <p className="text-white/60 text-xs mb-0.5">
                الموعد النهائي القادم:
              </p>
              <p className="text-white text-xs font-medium bg-orange-500/10 px-2 py-1 rounded-md line-clamp-1">
                {userData.nextDeadline}
              </p>
            </div>
          )}
        </div>

        {/* Course Progress */}
        {userData.courses.total > 0 && (
          <div className="w-full mt-1">
            <div className="w-full bg-white/10 rounded-full h-2.5 mb-1">
              <div
                className="bg-gradient-to-r from-blue-500 to-purple-500 h-2.5 rounded-full"
                style={{
                  width: `${
                    (userData.courses.completed / userData.courses.total) * 100
                  }%`,
                }}
              ></div>
            </div>
            <p className="text-xs text-white/60">
              {Math.round(
                (userData.courses.completed / userData.courses.total) * 100
              )}
              % من الدورات مكتملة
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={containerClasses}>
      {/* Background glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-blue-300/5 to-purple-500/10 opacity-50 rounded-xl animate-[pulse_4s_ease-in-out_infinite]"></div>

      {/* Widget content */}
      <div className="w-full h-full overflow-y-auto scrollbar-hide relative z-10">
        {renderICloudStyleCard()}
      </div>

      {/* Optional header button */}
      {showHeader && (
        <div className="absolute top-3 right-3 z-20">
          <button
            onClick={() => setShowHeader(false)}
            className="text-white/50 hover:text-white rounded-full p-1"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default UserCardWidget;
